/**
 * Why the app is starting: fresh, or because an account switch reloaded it.
 *
 * Switching account ends in a full page load, which tears down the switch skeleton and boots
 * the app from scratch. Left alone the person sees the switch skeleton, then whatever the
 * boot draws, for one action — two loading screens where there should be one.
 *
 * Read once here, when this module loads, rather than inside a component: React calls state
 * initialisers twice in development, and the second call would find the flag already taken.
 */

const SWITCH_KEY = "account-switch-reload"

/** Call immediately before the reload an account switch triggers. */
export const markAccountSwitch = () => {
  try {
    window.sessionStorage.setItem(SWITCH_KEY, "1")
  } catch {
    // Private windows and blocked storage: the app boots as it would have before.
  }
}

/** True when this page load is the one an account switch asked for. */
export const switchedAccount = (() => {
  try {
    if (window.sessionStorage.getItem(SWITCH_KEY) === null) return false
    window.sessionStorage.removeItem(SWITCH_KEY)
    return true
  } catch {
    return false
  }
})()

/* ------------------------------------------------------------ splash on demand -- */

type SplashListener = () => void

const splashListeners = new Set<SplashListener>()

/**
 * Plays the boot logo now, without a page load.
 *
 * Signing in navigates within the app rather than reloading it, so the splash that plays on a
 * fresh load never sees it. This is how the login tells it to play.
 */
export const playBootSplash = () => {
  splashListeners.forEach((listener) => listener())
}

/** Subscribes to `playBootSplash`. Returns the unsubscribe. */
export const onBootSplashRequested = (listener: SplashListener) => {
  splashListeners.add(listener)
  return () => {
    splashListeners.delete(listener)
  }
}
